import {
  AutoGraph,
  Add,
  ContentCopy,
  Forum,
  People,
  PlayArrow,
  Refresh,
  Search,
  Visibility,
  Psychology,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageItem } from "../components/chats/MessageItem";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import { DonutCard } from "../components/metrics/DonutCard";
import { MetricCardHorizontal } from "../components/metrics/MetricCardHorizontal";
import { TicketMinuteLineCard } from "../components/metrics/TicketMinuteLineCard";
import {
  dashboardPagePaddingSx,
  dashboardPageSx,
} from "../components/layout/dashboardStyles";
import { PageLoadingState } from "../components/layout/PageLoadingState";
import { CreateConversationAnalyticsJobParams } from "../data/api/ConversationAnalyticsApi";
import {
  ConversationAnalyticsConfig,
  ConversationAnalyticsFinalOutcome,
  ConversationAnalyticsFrictionType,
  ConversationAnalyticsFrictionSignal,
  ConversationAnalyticsIntent,
  ConversationAnalyticsJobAnalysisConfig,
  ConversationAnalyticsOutcome,
  ConversationAnalyticsOrderTakingStatus,
  ConversationAnalyticsSupportCategory,
  ConversationServiceAnalysis,
} from "../data/models/Analytics/ConversationAnalyticsModels";
import {
  MessageTypeEnum,
} from "../data/models/Chat/MessageModel";
import { TypeRoleEnum } from "../data/models/Chat/TypeRoleEnum";
import { getArrayTotal } from "../utils/ArrayTool";
import { getMonthName } from "../utils/DateTool";
import { useServiceMessages } from "../data/providers/FirestoreProvider";
import { useSoulAccess } from "../data/providers/SoulProvider";
import { useAnalytics } from "./hooks/useAnalytics";
import { useConversationAnalytics } from "./hooks/useConversationAnalytics";

const OUTCOME_LABELS: Record<ConversationAnalyticsOutcome, string> = {
  order_completed: "Pedido enviado",
  failed_order: "Pedido fallido",
  menu_only: "Solo carta",
  support_created: "Soporte",
  address_issue: "Dirección",
  payment_issue: "Pago",
  abandoned: "Sin respuesta",
  other: "Otro",
};

const INTENT_LABELS: Record<ConversationAnalyticsIntent, string> = {
  menu_request: "Carta",
  place_order: "Pedido",
  support: "Soporte",
  reserve: "Reserva",
  general_info: "Consulta",
  other: "Otro",
};

const SUPPORT_CATEGORY_LABELS: Record<ConversationAnalyticsSupportCategory, string> =
  {
    none: "General",
    not_delivered: "No entregado",
    bad_condition: "Mal estado",
    wrong_product: "Producto incorrecto",
    delay: "Retraso",
    refund: "Reembolso",
    payment: "Pago",
    address: "Dirección",
    general: "General",
    other: "Otro",
  };

const FINAL_OUTCOME_LABELS: Record<ConversationAnalyticsFinalOutcome, string> = {
  order_sent_to_preparation: "Pedido enviado",
  order_not_sent: "Pedido no enviado",
  menu_only: "Solo carta",
  support_created: "Soporte creado",
  normal_closure: "Cierre normal",
  customer_abandoned: "Cliente abandono",
  blocked: "Bloqueado",
  other: "Otro",
};

const FRICTION_LABELS: Record<ConversationAnalyticsFrictionType, string> = {
  none: "Sin fricción",
  address: "Dirección",
  coverage: "Cobertura",
  payment: "Pago",
  transfer_receipt: "Comprobante",
  business_hours: "Fuera de horario",
  product_unavailable: "Producto no disponible",
  support: "Soporte",
  abandonment: "Abandono",
  order_failed: "Pedido fallido",
  unknown: "Sin clasificar",
};

const FRICTION_SIGNAL_LABELS: Record<
  ConversationAnalyticsFrictionSignal,
  string
> = {
  address_not_found: "Dirección no encontrada",
  coverage_failed: "Sin cobertura",
  payment_method_unclear: "Método de pago poco claro",
  payment_prompt_repeated: "Pago repetido",
  payment_missing: "Falta pago",
  transfer_receipt_missing: "Falta comprobante",
  tool_failure: "Fallo de herramienta",
  assistant_loop: "Bucle del bot",
  customer_confusion: "Cliente confundido",
  support_escalated: "Escalado a soporte",
  product_unavailable: "Producto no disponible",
  outside_business_hours: "Fuera de horario",
  order_not_sent: "Pedido no enviado",
  handoff_confusion: "Confusion de traspaso",
  other: "Otro",
};

const PAYMENT_LABELS: Record<string, string> = {
  CASH: "Efectivo",
  TRANSFER: "Transferencia",
};

const DELIVERY_LABELS: Record<
  ConversationServiceAnalysis["orderJourney"]["deliveryPreference"],
  string
> = {
  delivery: "Domicilio",
  pickup: "Pickup",
  unknown: "Sin definir",
};

const chipSx = { borderRadius: 1, height: 24 };

const analyticsContentPanelSx = {
  width: "100%",
  minWidth: 0,
  p: { xs: 1.25, sm: 1.5 },
  borderRadius: 1,
  bgcolor: "background.paper",
  overflow: "hidden",
};

const analyticsMonthToolbarSx = {
  p: { xs: 1.25, sm: 1.5 },
  borderRadius: 1,
  bgcolor: "action.hover",
  width: "100%",
};

const ORDER_TAKING_STATUS_LABELS: Record<
  ConversationAnalyticsOrderTakingStatus,
  string
> = {
  sent: "Enviado a preparación",
  error: "No enviado",
  none: "Sin intentó",
};

const ORDER_TAKING_STATUS_COLORS: Record<
  ConversationAnalyticsOrderTakingStatus,
  "success" | "warning" | "default"
> = {
  sent: "success",
  error: "warning",
  none: "default",
};

const SERVICE_TAG_LABELS: Record<string, string> = {
  customer_ordered: "Pedido",
  order_sent_to_preparation: "Pedido enviado",
  order_failed: "Pedido fallido",
  support_ticket_created: "Ticket soporte",
  support_after_order: "Soporte post pedido",
  support_requested: "Soporte pedido",
  support_created: "Soporte",
  menu_requested: "Carta solicitada",
  menu_sent: "Carta enviada",
  product_requested: "Producto pedido",
  product_added: "Producto agregado",
  product_unavailable: "Producto no disponible",
  delivery: "Domicilio",
  pickup: "Pickup",
  address_resolved: "Dirección resuelta",
  coverage_failed: "Sin cobertura",
  payment_selected: "Pago",
  payment_resolved: "Pago resuelto",
  outside_business_hours: "Fuera de horario",
  recurrent_customer: "Cliente recurrente",
  friction: "Friccion",
  support: "Soporte",
  blocked: "Bloqueado",
  customer_abandoned: "Abandono",
  normal_closure: "Cierre normal",
  menu_only: "Solo carta",
  other: "Otro",
};

const HIDDEN_SERVICE_TAGS = new Set([
  "menu_only",
  "support_created",
  "normal_closure",
  "customer_abandoned",
  "blocked",
  "other",
  "order_not_sent",
]);

const getServiceSummaryLabel = (service: ConversationServiceAnalysis) =>
  service.summary ||
  service.reasonForContact ||
  INTENT_LABELS[service.primaryIntent || service.intent];

const getOrderTakingStatusLabel = (
  status: ConversationAnalyticsOrderTakingStatus,
) => ORDER_TAKING_STATUS_LABELS[status];

const getOrderTakingStatusColor = (
  status: ConversationAnalyticsOrderTakingStatus,
) => ORDER_TAKING_STATUS_COLORS[status];

const getSupportTicketsLabel = (count: number) =>
  count === 0 ? "Sin tickets" : count === 1 ? "1 ticket" : `${count} tickets`;

const getSupportTicketsColor = (count: number) =>
  count > 0 ? ("success" as const) : ("default" as const);

const formatServiceTagLabel = (tag: string) =>
  SERVICE_TAG_LABELS[tag] ||
  tag
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getVisibleServiceTags = (tags: string[]) =>
  tags.filter(
    (tag) => !HIDDEN_SERVICE_TAGS.has(tag) && !tag.startsWith("friction_"),
  );

const formatFrictionSignalLabel = (signal: string) =>
  FRICTION_SIGNAL_LABELS[signal as ConversationAnalyticsFrictionSignal] ||
  signal
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getSupportCategoryLabel = (
  category: ConversationAnalyticsSupportCategory,
) => SUPPORT_CATEGORY_LABELS[category];

type SortDirection = "asc" | "desc";

type SortConfig<T extends string> = {
  orderBy: T;
  direction: SortDirection;
};

type ServiceSortColumn =
  | "phoneNumber"
  | "summary"
  | "orderTakingStatus"
  | "hasFriction"
  | "deliveryRegistration"
  | "paymentRegistration"
  | "supportTicketsCount"
  | "serviceTags"
  | "createdAt";

type CustomerSortColumn =
  | "phoneNumber"
  | "servicesCount"
  | "ordersCompleted"
  | "frictions"
  | "lastFinalOutcome"
  | "updatedAt";

type JobSortColumn =
  | "dateFrom"
  | "status"
  | "batchSize"
  | "processedCount"
  | "failedCount"
  | "createdAt";

const normalizeSearchText = (value: unknown) =>
  value == null
    ? ""
    : String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const compareSortableValues = (left: unknown, right: unknown) => {
  const leftNumber =
    typeof left === "number"
      ? left
      : left instanceof Date
        ? left.getTime()
        : Number.NaN;
  const rightNumber =
    typeof right === "number"
      ? right
      : right instanceof Date
        ? right.getTime()
        : Number.NaN;

  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return normalizeSearchText(left).localeCompare(normalizeSearchText(right), "es", {
    numeric: true,
    sensitivity: "base",
  });
};

const sortTableRows = <T, K extends string>(
  rows: T[],
  sort: SortConfig<K>,
  accessors: Record<K, (row: T) => unknown>
) =>
  [...rows].sort((left, right) => {
    const result = compareSortableValues(
      accessors[sort.orderBy](left),
      accessors[sort.orderBy](right)
    );
    return sort.direction === "asc" ? result : -result;
  });

const paginateRows = <T,>(rows: T[], page: number, rowsPerPage: number): T[] =>
  rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

const useTablePagination = (rowsPerPageDefault = 10) => {
  const [pagination, setPagination] = React.useState({
    page: 0,
    rowsPerPage: rowsPerPageDefault,
  });

  const setPage = React.useCallback((page: number) => {
    setPagination((current) => ({ ...current, page }));
  }, []);

  const setRowsPerPage = React.useCallback((rowsPerPage: number) => {
    setPagination({ page: 0, rowsPerPage });
  }, []);

  return {
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    setPage,
    setRowsPerPage,
  };
};

const renderSortableHeaderCell = <T extends string>(
  label: string,
  column: T,
  sort: SortConfig<T>,
  onSort: (column: T) => void,
  align: "left" | "right" | "center" = "left",
  sx?: Record<string, unknown>
) => (
  <TableCell align={align} sx={sx}>
    <TableSortLabel
      active={sort.orderBy === column}
      direction={sort.orderBy === column ? sort.direction : "asc"}
      onClick={() => onSort(column)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

const toTimestamp = (value?: Date | string | null) => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const formatDate = (value?: Date | string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-CO", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDateTime = (value?: Date | string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-CO", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const yesNoLabel = (value: boolean | null) => {
  if (value == null) return "No aplica";
  return value ? "Si" : "No";
};

const getTriStateChipColor = (value: boolean | null) => {
  if (value == null) return "default" as const;
  return value ? ("success" as const) : ("warning" as const);
};

const getDeliveryRegistrationInfo = (
  service: ConversationServiceAnalysis,
) => {
  if (service.orderJourney.deliveryPreference === "delivery") {
    return {
      value: service.orderJourney.addressResolved,
      sub: DELIVERY_LABELS.delivery,
      sortValue:
        service.orderJourney.addressResolved == null
          ? -1
          : Number(service.orderJourney.addressResolved),
    };
  }

  if (service.orderJourney.deliveryPreference === "pickup") {
    return {
      value: service.orderJourney.pickupResolved,
      sub: DELIVERY_LABELS.pickup,
      sortValue:
        service.orderJourney.pickupResolved == null
          ? -1
          : Number(service.orderJourney.pickupResolved),
    };
  }

  return {
    value: service.orderTakingStatus === "none" ? null : false,
    sub: service.orderTakingStatus === "none" ? "Sin intentó" : "Sin definir",
    sortValue: service.orderTakingStatus === "none" ? -1 : 0,
  };
};

const getPaymentRegistrationInfo = (
  service: ConversationServiceAnalysis,
) => {
  const paymentMethod =
    service.orderJourney.paymentMethod || service.orderDetails.paymentMethod;

  return {
    value: paymentMethod ? true : service.orderTakingStatus === "none" ? null : false,
    sub: paymentMethod
      ? PAYMENT_LABELS[paymentMethod] || paymentMethod
      : service.orderTakingStatus === "none"
        ? "Sin intentó"
        : "Sin método",
    sortValue: paymentMethod ? 1 : service.orderTakingStatus === "none" ? -1 : 0,
  };
};

const getDefaultJobAnalysisConfig = (
  config?: ConversationAnalyticsConfig
): ConversationAnalyticsJobAnalysisConfig => ({
  analysisDimensions: {
    semanticQuality: config?.analysisDimensions.semanticQuality ?? true,
    frustration: config?.analysisDimensions.frustration ?? true,
    unresolvedReason: config?.analysisDimensions.unresolvedReason ?? true,
    nextBestAction: config?.analysisDimensions.nextBestAction ?? true,
  },
  weights: {
    deterministic: config?.weights.deterministic ?? 60,
    semantic: config?.weights.semantic ?? 40,
  },
  thresholds: {
    abandonedAfterAssistantMinutes:
      config?.thresholds.abandonedAfterAssistantMinutes ?? 30,
  },
});

const buildJobDraft = (
  config: ConversationAnalyticsConfig | undefined,
  dateFrom: string,
  dateTo: string
): CreateConversationAnalyticsJobParams => ({
  dateFrom,
  dateTo,
  force: false,
  runNow: true,
  batchSize: config?.batchSize ?? 8,
  maxBatches: 1,
  analysisConfig: getDefaultJobAnalysisConfig(config),
});

const AnalyticsStatCard = ({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: 1,
      height: "100%",
      width: "100%",
    }}
  >
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const DetailSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      width: "100%",
      minWidth: 0,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: { xs: 1.5, sm: 2 },
      height: "100%",
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
      {title}
    </Typography>
    <Stack spacing={1}>{children}</Stack>
  </Box>
);

const InsightCard = ({
  label,
  value,
  sub,
  minWidth = 240,
  selected = false,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  minWidth?: number;
  selected?: boolean;
  onClick?: () => void;
}) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: { xs: 1.5, sm: 1.75 },
      borderRadius: 1.5,
      width: "100%",
      minWidth: { xs: 0, sm: minWidth },
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: { xs: "100%", sm: `${minWidth}px` },
      cursor: onClick ? "pointer" : "default",
      borderColor: selected ? "primary.main" : "divider",
      bgcolor: selected ? "action.hover" : "background.paper",
      transition: "border-color 0.15s ease, background-color 0.15s ease",
      "&:hover": onClick
        ? {
            borderColor: "primary.main",
            bgcolor: "action.hover",
          }
        : undefined,
    }}
  >
    <Stack spacing={0.75}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Box sx={{ minHeight: 44 }}>{value}</Box>
      {sub ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {sub}
        </Typography>
      ) : null}
    </Stack>
  </Paper>
);

const AnalyticsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isConversationsRoute = location.pathname.endsWith("/conversations");
  const section = isConversationsRoute ? "conversations" : "summary";
  const { plan, loading, showCurrent, setShowCurrent } = useAnalytics(
    section === "summary"
  );
  const conversation = useConversationAnalytics(section === "conversations");
  const { soul, loading: soulLoading } = useSoulAccess();
  const [conversationTab, setConversationTab] = useState(0);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [jobDraft, setJobDraft] = useState<CreateConversationAnalyticsJobParams | null>(
    null
  );
  const [selectedService, setSelectedService] =
    useState<ConversationServiceAnalysis | null>(null);
  const [selectedCustomerPhone, setSelectedCustomerPhone] = useState<
    string | null
  >(null);
  const [serviceSearch, setServiceSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [copyFeedback, setCopyFeedback] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [serviceSort, setServiceSort] = useState<
    SortConfig<ServiceSortColumn>
  >({
    orderBy: "createdAt",
    direction: "desc",
  });
  const [customerSort, setCustomerSort] = useState<
    SortConfig<CustomerSortColumn>
  >({
    orderBy: "updatedAt",
    direction: "desc",
  });
  const [jobSort, setJobSort] = useState<SortConfig<JobSortColumn>>({
    orderBy: "createdAt",
    direction: "desc",
  });
  const servicesPagination = useTablePagination(10);
  const customersPagination = useTablePagination(10);
  const jobsPagination = useTablePagination(10);
  const { setPage: setServicesPage } = servicesPagination;
  const { setPage: setCustomersPage } = customersPagination;
  const company = soul?.company ?? "";
  const selectedServiceMessages = useServiceMessages(
    company,
    selectedService?.serviceCode
  );
  const conversationDateFrom = conversation.dateFrom;
  const conversationDateTo = conversation.dateTo;
  const loadCustomerTimeline = conversation.loadCustomerTimeline;

  const handleCopyOrderId = async (orderId: string) => {
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(orderId);
      } else if (typeof document !== "undefined") {
        const textArea = document.createElement("textarea");
        textArea.value = orderId;
        textArea.setAttribute("readonly", "true");
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopyFeedback({
        open: true,
        message: "ID de orden copiado",
        severity: "success",
      });
    } catch {
      setCopyFeedback({
        open: true,
        message: "No se pudo copiar el ID de orden",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    setServicesPage(0);
  }, [
    conversation.dateFrom,
    conversation.dateTo,
    serviceSearch,
    setServicesPage,
  ]);

  useEffect(() => {
    setCustomersPage(0);
  }, [customerSearch, setCustomersPage]);

  const relatedServices = React.useMemo(() => {
    if (!selectedService) return [];

    return [...conversation.services]
      .filter((service) => service.phoneNumber === selectedService.phoneNumber)
      .sort((left, right) => {
        const leftTimestamp = toTimestamp(left.createdAt ?? left.analyzedAt);
        const rightTimestamp = toTimestamp(right.createdAt ?? right.analyzedAt);
        if (leftTimestamp !== rightTimestamp) {
          return leftTimestamp - rightTimestamp;
        }
        return left.serviceCode.localeCompare(right.serviceCode);
      });
  }, [conversation.services, selectedService]);

  const sortedServices = React.useMemo(
    () =>
      sortTableRows(conversation.services, serviceSort, {
        phoneNumber: (entry) => entry.phoneNumber || "",
        summary: (entry) => getServiceSummaryLabel(entry),
        orderTakingStatus: (entry) =>
          ({
            none: 0,
            error: 1,
            sent: 2,
          })[entry.orderTakingStatus],
        hasFriction: (entry) => (entry.frictionType === "none" ? 0 : 1),
        deliveryRegistration: (entry) =>
          getDeliveryRegistrationInfo(entry).sortValue,
        paymentRegistration: (entry) =>
          getPaymentRegistrationInfo(entry).sortValue,
        supportTicketsCount: (entry) => Number(entry.supportTicketsCount || 0),
        serviceTags: (entry) => entry.serviceTags?.join(", ") || "",
        createdAt: (entry) => toTimestamp(entry.createdAt ?? entry.analyzedAt),
      }),
    [conversation.services, serviceSort]
  );

  const sortedCustomers = React.useMemo(
    () =>
      sortTableRows(conversation.customers, customerSort, {
        phoneNumber: (entry) => entry.phoneNumber || "",
        servicesCount: (entry) => Number(entry.servicesCount || 0),
        ordersCompleted: (entry) => Number(entry.ordersCompleted || 0),
        frictions: (entry) =>
          Number(
            entry.frictionCount ??
              Number(entry.failedOrders || 0) +
                Number(entry.addressIssues || 0) +
                Number(entry.paymentIssues || 0) +
                Number(entry.abandonedCount || 0)
          ),
        lastFinalOutcome: (entry) =>
          entry.lastFinalOutcome
            ? FINAL_OUTCOME_LABELS[entry.lastFinalOutcome]
            : OUTCOME_LABELS[entry.lastOutcome] || entry.lastOutcome,
        updatedAt: (entry) => toTimestamp(entry.updatedAt),
      }),
    [conversation.customers, customerSort]
  );

  const sortedJobs = React.useMemo(
    () =>
      sortTableRows(conversation.jobs, jobSort, {
        dateFrom: (entry) => entry.dateFrom || "",
        status: (entry) => entry.status || "",
        batchSize: (entry) => Number(entry.batchSize || 0),
        processedCount: (entry) => Number(entry.processedCount || 0),
        failedCount: (entry) => Number(entry.failedCount || 0),
        createdAt: (entry) => toTimestamp(entry.createdAt),
      }),
    [conversation.jobs, jobSort]
  );

  const normalizedServiceSearch = normalizeSearchText(serviceSearch);
  const normalizedServiceSearchDigits = normalizedServiceSearch.replace(
    /\D/g,
    ""
  );

  const normalizedCustomerSearch = normalizeSearchText(customerSearch);
  const normalizedCustomerSearchDigits = normalizedCustomerSearch.replace(
    /\D/g,
    ""
  );

  const filteredServices = React.useMemo(() => {
    if (!normalizedServiceSearch) {
      return sortedServices;
    }

    return sortedServices.filter((service) => {
      const deliveryRegistrationInfo = getDeliveryRegistrationInfo(service);
      const paymentRegistrationInfo = getPaymentRegistrationInfo(service);
      const searchableValues = [
        service.phoneNumber,
        service.serviceCode,
        getServiceSummaryLabel(service),
        INTENT_LABELS[service.primaryIntent || service.intent],
        getOrderTakingStatusLabel(service.orderTakingStatus),
        service.frictionType === "none"
          ? "Sin fricción"
          : FRICTION_LABELS[service.frictionType],
        service.deterministicFrictionType
          ? `Regla base ${FRICTION_LABELS[service.deterministicFrictionType]}`
          : "",
        service.frictionReason || "",
        ...(service.frictionSignals ?? []).map(formatFrictionSignalLabel),
        deliveryRegistrationInfo.sub,
        paymentRegistrationInfo.sub,
        getSupportTicketsLabel(service.supportTicketsCount),
        service.supportDetails.ticketCode || "",
        service.orderDetails.orderId || "",
        service.nextBestAction || "",
        service.summary || "",
        service.closureReason || "",
        service.unresolvedReason || "",
        formatDate(service.createdAt ?? service.analyzedAt),
        ...getVisibleServiceTags(service.serviceTags).map(formatServiceTagLabel),
      ].map((value) => String(value ?? ""));
      const searchableText = normalizeSearchText(searchableValues.join(" "));
      if (searchableText.includes(normalizedServiceSearch)) {
        return true;
      }

      if (!normalizedServiceSearchDigits) {
        return false;
      }

      const searchableDigits = searchableValues.join(" ").replace(/\D/g, "");
      return searchableDigits.includes(normalizedServiceSearchDigits);
    });
  }, [normalizedServiceSearch, normalizedServiceSearchDigits, sortedServices]);

  const filteredCustomers = React.useMemo(() => {
    if (!normalizedCustomerSearch) {
      return sortedCustomers;
    }

    return sortedCustomers.filter((customer) => {
      const frictions =
        customer.frictionCount ??
        Number(customer.failedOrders || 0) +
          Number(customer.addressIssues || 0) +
          Number(customer.paymentIssues || 0) +
          Number(customer.abandonedCount || 0);

      const searchableValues = [
        customer.phoneNumber,
        customer.servicesCount,
        customer.ordersCompleted,
        frictions,
        customer.lastFinalOutcome
          ? FINAL_OUTCOME_LABELS[customer.lastFinalOutcome]
          : OUTCOME_LABELS[customer.lastOutcome] || customer.lastOutcome,
        ...(customer.controlledTags || []),
        ...(customer.segments || []),
        formatDate(customer.updatedAt),
      ].map((value) => String(value ?? ""));

      const searchableText = normalizeSearchText(searchableValues.join(" "));
      if (searchableText.includes(normalizedCustomerSearch)) {
        return true;
      }

      if (!normalizedCustomerSearchDigits) {
        return false;
      }

      const searchableDigits = searchableValues.join(" ").replace(/\D/g, "");
      return searchableDigits.includes(normalizedCustomerSearchDigits);
    });
  }, [normalizedCustomerSearch, normalizedCustomerSearchDigits, sortedCustomers]);

  const visibleServices = paginateRows(
    filteredServices,
    servicesPagination.page,
    servicesPagination.rowsPerPage
  );
  const visibleCustomers = paginateRows(
    filteredCustomers,
    customersPagination.page,
    customersPagination.rowsPerPage
  );
  const visibleJobs = paginateRows(
    sortedJobs,
    jobsPagination.page,
    jobsPagination.rowsPerPage
  );

  const createSortHandler = <T extends string>(
    setSort: React.Dispatch<React.SetStateAction<SortConfig<T>>>,
    pagination: { setPage: (page: number) => void }
  ) => {
    return (column: T) => {
      setSort((current) =>
        current.orderBy === column
          ? {
              orderBy: current.orderBy,
              direction: current.direction === "asc" ? "desc" : "asc",
            }
          : { orderBy: column, direction: "asc" }
      );
      pagination.setPage(0);
    };
  };

  const handleServiceSort = createSortHandler(setServiceSort, servicesPagination);
  const handleCustomerSort = createSortHandler(
    setCustomerSort,
    customersPagination
  );
  const handleJobSort = createSortHandler(setJobSort, jobsPagination);

  const handleOpenJobDialog = () => {
    setJobDraft(
      buildJobDraft(
        conversation.config,
        conversation.dateFrom,
        conversation.dateTo
      )
    );
    setJobDialogOpen(true);
  };

  const updateJobDraft = (
    updater: (current: CreateConversationAnalyticsJobParams) => CreateConversationAnalyticsJobParams
  ) => {
    setJobDraft((current) => (current ? updater(current) : current));
  };

  const handleCreateJob = async () => {
    if (!jobDraft) return;
    const ok = await conversation.createJob(jobDraft);
    if (ok) {
      setJobDialogOpen(false);
    }
  };

  const handleOpenCustomerTimeline = (phoneNumber: string) => {
    setSelectedCustomerPhone(phoneNumber);
  };

  useEffect(() => {
    if (!selectedCustomerPhone || section !== "conversations") {
      return;
    }
    void loadCustomerTimeline(selectedCustomerPhone);
  }, [
    conversationDateFrom,
    conversationDateTo,
    loadCustomerTimeline,
    selectedCustomerPhone,
    section,
  ]);

  useEffect(() => {
    setSelectedService(null);
    setSelectedCustomerPhone(null);
    setJobDialogOpen(false);
  }, [section]);

  useEffect(() => {
    if (section === "conversations") {
      setConversationTab(0);
    }
  }, [section]);

  const handleOpenConversationFromCustomerPhone = (phoneNumber: string) => {
    setConversationTab(0);
    setServiceSearch(phoneNumber);
    setServicesPage(0);
  };

  const renderMonthHeader = ({
    showCurrentMonth,
    onShowCurrentMonthChange,
    onRefresh,
    loading,
    showRefreshButton,
  }: {
    showCurrentMonth: boolean;
    onShowCurrentMonthChange: (showCurrentMonth: boolean) => void;
    onRefresh?: () => void;
    loading?: boolean;
    showRefreshButton?: boolean;
  }) => (
    <Box sx={analyticsMonthToolbarSx}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant={!showCurrentMonth ? "contained" : "outlined"}
            onClick={() => onShowCurrentMonthChange(false)}
          >
            Mes anterior
          </Button>
          <Button
            size="small"
            variant={showCurrentMonth ? "contained" : "outlined"}
            onClick={() => onShowCurrentMonthChange(true)}
          >
            Mes actual
          </Button>
        </Box>
        <Typography
          sx={{ fontWeight: 700, my: { xs: 0.25, sm: 0 } }}
        >
          {showCurrentMonth ? getMonthName() : getMonthName(-1)}
        </Typography>
        {showRefreshButton && onRefresh ? (
          <Button
            size="small"
            startIcon={<Refresh />}
            variant="outlined"
            onClick={onRefresh}
            disabled={loading}
          >
            Actualizar
          </Button>
        ) : <Button size="small"></Button>}
      </Stack>
    </Box>
  );

  const renderSummaryView = () => (
    <Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
      {renderMonthHeader({
        showCurrentMonth: showCurrent,
        onShowCurrentMonthChange: setShowCurrent,
        showRefreshButton: false,
      })}

      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Actividad del Mes
        </Typography>
        <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
        {loading ? (
          <PageLoadingState label="Cargando actividad" compact />
        ) : plan ? (
          <Grid container spacing={{ xs: 1.25, sm: 2 }}>
            <Grid item xs={12} md={3}>
              <MetricCardHorizontal
                title="Usuarios"
                value={getArrayTotal(
                  plan.dailyActivity.usersPerDay
                ).toLocaleString("es-ES")}
                sub="Usuarios activos"
                data={plan.dailyActivity.usersPerDay}
                color="#8A00CC"
                areaColor="#561375ff"
                percent={+3}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCardHorizontal
                title="Chats"
                value={getArrayTotal(
                  plan.dailyActivity.chatsPerDay
                ).toLocaleString("es-ES")}
                sub="Nuevos chats iniciados"
                data={plan.dailyActivity.chatsPerDay}
                color="#8A00CC"
                areaColor="#561375ff"
                percent={-5}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCardHorizontal
                title="Mensajes"
                value={getArrayTotal(
                  plan.dailyActivity.messagesPerDay
                ).toLocaleString("es-ES")}
                sub="Mensajes enviados"
                data={plan.dailyActivity.messagesPerDay}
                color="#8A00CC"
                areaColor="#561375ff"
                percent={+25}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCardHorizontal
                title="Tokens"
                value={getArrayTotal(
                  plan.dailyActivity.tokensPerDay
                ).toLocaleString("es-ES")}
                sub="Tokens generados"
                data={plan.dailyActivity.tokensPerDay}
                color="#8A00CC"
                areaColor="#561375ff"
                percent={-5}
              />
            </Grid>
            <Grid item xs={12}>
              <TicketMinuteLineCard
                ticketsMinutes={plan.dailyActivity.ticketsPerDay}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DonutCard
                title="Chats del mes"
                data={[
                  {
                    value: getArrayTotal(plan.dailyActivity.chatsWithOrders),
                    label: "Ventas",
                    color: "#cd7af3ff",
                  },
                  {
                    value:
                      getArrayTotal(plan.dailyActivity.chatsPerDay) -
                      getArrayTotal(plan.dailyActivity.chatsWithOrders),
                    label: "Otros",
                    color: "#8A00CC",
                  },
                ]}
                totalLabel="Total chats"
                centerLabel="Ventas"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DonutCard
                title="Ventas"
                data={[
                  {
                    value: getArrayTotal(plan.dailyActivity.chatsWithDelivery),
                    label: "Domicilio",
                    color: "#cd7af3ff",
                  },
                  {
                    value:
                      getArrayTotal(plan.dailyActivity.chatsWithOrders) -
                      getArrayTotal(plan.dailyActivity.chatsWithDelivery),
                    label: "Pickup",
                    color: "#8A00CC",
                  },
                ]}
                totalLabel="Total ventas"
                centerLabel="Domicilio"
              />
            </Grid>
          </Grid>
        ) : (
          <Typography>No hay datos para este mes</Typography>
        )}
      </Box>
    </Stack>
  );

  const renderConversationsView = () => (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Grid
        container
        spacing={{ xs: 1.25, sm: 2 }}
        sx={{ mb: { xs: 1.5, sm: 2 }, width: "100%", m: 0 }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsStatCard
            label="Servicios analizados"
            value={conversation.overview?.servicesAnalyzed || 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsStatCard
            label="Enviados a preparación"
            value={conversation.overview?.ordersSentCount || 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsStatCard
            label="Tickets soporte"
            value={conversation.overview?.supportCount || 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsStatCard
            label="Servicios con fricción"
            value={
              (conversation.overview?.servicesAnalyzed || 0) -
              (conversation.overview?.frictionBreakdown?.none || 0)
            }
          />
        </Grid>
      </Grid>

      {conversation.overview?.frictionSignalBreakdown?.length ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 1,
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          <Stack spacing={1}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Señales de fricción más comunes
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Patrones semánticos útiles para ajustar prompts, agentes y herramientas.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {conversation.overview?.frictionSignalBreakdown?.map((item) => (
                <Chip
                  key={item.signal}
                  size="small"
                  sx={chipSx}
                  label={`${formatFrictionSignalLabel(item.signal)} - ${item.count}`}
                />
              ))}
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      <Paper
        variant="outlined"
        sx={{ borderRadius: 1, overflow: "hidden", width: "100%" }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 1.25,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Servicios
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {serviceSearch.trim()
                ? `${filteredServices.length} de ${conversation.servicesTotal} resultados`
                : `${conversation.servicesTotal} resultados`}
            </Typography>
          </Box>
          <TextField
            label="Buscar en la tabla"
            size="small"
            value={serviceSearch}
            onChange={(event) => setServiceSearch(event.target.value)}
            placeholder="teléfono, pedido, tag..."
            sx={{
              width: { xs: "100%", md: 360 },
              maxWidth: "100%",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 1090, tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                {renderSortableHeaderCell(
                  "Cliente",
                  "phoneNumber",
                  serviceSort,
                  handleServiceSort,
                  "left",
                  { width: 110 }
                )}
                {renderSortableHeaderCell(
                  "Resumen del servicio",
                  "summary",
                  serviceSort,
                  handleServiceSort,
                  "left",
                  { width: 220 }
                )}
                {renderSortableHeaderCell(
                  "Hubo fricción",
                  "hasFriction",
                  serviceSort,
                  handleServiceSort,
                  "center",
                  { width: 90 }
                )}
                {renderSortableHeaderCell(
                  "Toma pedidos",
                  "orderTakingStatus",
                  serviceSort,
                  handleServiceSort,
                  "center",
                  { width: 150 }
                )}
                {renderSortableHeaderCell(
                  "Dirección / pickup",
                  "deliveryRegistration",
                  serviceSort,
                  handleServiceSort,
                  "center",
                  { width: 110 }
                )}
                {renderSortableHeaderCell(
                  "Método de pago",
                  "paymentRegistration",
                  serviceSort,
                  handleServiceSort,
                  "center",
                  { width: 110 }
                )}
                {renderSortableHeaderCell(
                  "Tickets soporte",
                  "supportTicketsCount",
                  serviceSort,
                  handleServiceSort,
                  "right",
                  { width: 100 }
                )}
                {renderSortableHeaderCell(
                  "Fecha",
                  "createdAt",
                  serviceSort,
                  handleServiceSort,
                  "left",
                  { width: 100 }
                )}
                <TableCell align="right" sx={{ width: 100 }}>
                  Acción
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleServices.map((service) => {
                const deliveryRegistrationInfo =
                  getDeliveryRegistrationInfo(service);
                const paymentRegistrationInfo =
                  getPaymentRegistrationInfo(service);

                return (
                  <TableRow key={service.serviceCode} hover>
                    <TableCell sx={{ width: 110 }}>{service.phoneNumber}</TableCell>
                    <TableCell sx={{ width: 220 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getServiceSummaryLabel(service)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {INTENT_LABELS[service.primaryIntent || service.intent]}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 90 }}>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Chip
                          size="small"
                          sx={chipSx}
                          color={service.frictionType === "none" ? "default" : "warning"}
                          label={yesNoLabel(service.frictionType !== "none")}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 150 }}>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Chip
                          size="small"
                          sx={chipSx}
                          color={getOrderTakingStatusColor(service.orderTakingStatus)}
                          label={getOrderTakingStatusLabel(
                            service.orderTakingStatus
                          )}
                        />
                        {service.orderDetails.orderId ? (
                          <Tooltip title="Copiar ID de orden">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopyOrderId(service.orderDetails.orderId!)
                              }
                              aria-label="Copiar ID de orden"
                              sx={{
                                color: "secondary.main",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                              }}
                            >
                              <ContentCopy fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 110 }}>
                      <Stack spacing={0.35} alignItems="center">
                        <Chip
                          size="small"
                          sx={chipSx}
                          color={getTriStateChipColor(
                            deliveryRegistrationInfo.value
                          )}
                          label={yesNoLabel(deliveryRegistrationInfo.value)}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {deliveryRegistrationInfo.sub}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 110 }}>
                      <Stack spacing={0.35} alignItems="center">
                        <Chip
                          size="small"
                          sx={chipSx}
                          color={getTriStateChipColor(
                            paymentRegistrationInfo.value
                          )}
                          label={yesNoLabel(paymentRegistrationInfo.value)}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {paymentRegistrationInfo.sub}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>
                      <Chip
                        size="small"
                        sx={chipSx}
                        color={getSupportTicketsColor(service.supportTicketsCount)}
                        label={getSupportTicketsLabel(service.supportTicketsCount)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>
                      {formatDate(service.createdAt ?? service.analyzedAt)}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Detalle">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedService(service)}
                            aria-label="Ver detalle"
                            sx={{
                              color: "common.white",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                            }}
                          >
                            <Visibility fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!visibleServices.length && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography sx={{ color: "text.secondary", py: 2 }}>
                      Sin servicios analizados en este rango.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredServices.length}
          page={servicesPagination.page}
          onPageChange={(_event, page) => servicesPagination.setPage(page)}
          rowsPerPage={servicesPagination.rowsPerPage}
          onRowsPerPageChange={(event) =>
            servicesPagination.setRowsPerPage(Number(event.target.value))
          }
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            "& .MuiTablePagination-toolbar": {
              flexWrap: "wrap",
              gap: 0.75,
              py: { xs: 0.5, sm: 1 },
            },
            "& .MuiTablePagination-spacer": {
              flex: "1 1 auto",
            },
          }}
        />
      </Paper>
    </Box>
  );

  const renderCustomersView = () => (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Grid
        container
        spacing={{ xs: 1.25, sm: 2 }}
        sx={{ mb: { xs: 1.5, sm: 2 }, width: "100%", m: 0 }}
      >
        <Grid item xs={12} sm={6} md={6}>
          <AnalyticsStatCard
            label="Clientes con perfil"
            value={conversation.customersTotal}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <AnalyticsStatCard
            label="Recurrentes"
            value={conversation.overview?.recurrentCustomersCount || 0}
          />
        </Grid>
      </Grid>

      <Paper
        variant="outlined"
        sx={{ borderRadius: 1, overflow: "hidden", width: "100%" }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 1.25,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Clientes
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {customerSearch.trim()
                ? `${filteredCustomers.length} de ${conversation.customersTotal} resultados`
                : `${conversation.customersTotal} resultados`}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <TextField
              label="Buscar cliente"
              size="small"
              value={customerSearch}
              onChange={(event) => setCustomerSearch(event.target.value)}
              placeholder="teléfono, resultado, tag..."
              sx={{
                width: { xs: "100%", md: 360 },
                maxWidth: "100%",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>
        <Divider />
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 780 }}>
            <TableHead>
              <TableRow>
                {renderSortableHeaderCell(
                  "Cliente",
                  "phoneNumber",
                  customerSort,
                  handleCustomerSort,
                  "left",
                  { width: 150 }
                )}
                {renderSortableHeaderCell(
                  "Servicios",
                  "servicesCount",
                  customerSort,
                  handleCustomerSort,
                  "right",
                  { width: 90 }
                )}
                {renderSortableHeaderCell(
                  "Pedidos",
                  "ordersCompleted",
                  customerSort,
                  handleCustomerSort,
                  "right",
                  { width: 90 }
                )}
                {renderSortableHeaderCell(
                  "Fricciones",
                  "frictions",
                  customerSort,
                  handleCustomerSort,
                  "right",
                  { width: 100 }
                )}
                {renderSortableHeaderCell(
                  "Ultimo resultado",
                  "lastFinalOutcome",
                  customerSort,
                  handleCustomerSort,
                  "left",
                  { width: 190 }
                )}
                {renderSortableHeaderCell(
                  "Actualizado",
                  "updatedAt",
                  customerSort,
                  handleCustomerSort,
                  "left",
                  { width: 150 }
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCustomers.map((customer) => (
                <TableRow
                  key={customer.phoneNumber}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleOpenCustomerTimeline(customer.phoneNumber)}
                >
                  <TableCell sx={{ width: 150 }}>
                    <Tooltip title="Ir a conversaciones con este número">
                      <Button
                        variant="text"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenConversationFromCustomerPhone(
                            customer.phoneNumber
                          );
                        }}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          textTransform: "none",
                          fontWeight: 700,
                          color: "secondary.main",
                          justifyContent: "flex-start",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {customer.phoneNumber}
                      </Button>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>
                    {customer.servicesCount}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>
                    {customer.ordersCompleted}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>
                    {customer.frictionCount ??
                      customer.failedOrders +
                        customer.addressIssues +
                        customer.paymentIssues +
                        customer.abandonedCount}
                  </TableCell>
                  <TableCell sx={{ width: 190 }}>
                    {customer.lastFinalOutcome
                      ? FINAL_OUTCOME_LABELS[customer.lastFinalOutcome]
                      : OUTCOME_LABELS[customer.lastOutcome]}
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>{formatDate(customer.updatedAt)}</TableCell>
                </TableRow>
              ))}
              {!visibleCustomers.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography sx={{ color: "text.secondary", py: 2 }}>
                      Sin perfiles de cliente todavía.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredCustomers.length}
          page={customersPagination.page}
          onPageChange={(_event, page) => customersPagination.setPage(page)}
          rowsPerPage={customersPagination.rowsPerPage}
          onRowsPerPageChange={(event) =>
            customersPagination.setRowsPerPage(Number(event.target.value))
          }
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            "& .MuiTablePagination-toolbar": {
              flexWrap: "wrap",
              gap: 0.75,
              py: { xs: 0.5, sm: 1 },
            },
            "& .MuiTablePagination-spacer": {
              flex: "1 1 auto",
            },
          }}
        />
      </Paper>
    </Box>
  );

  const renderConversationSection = () => (
    <Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
      {renderMonthHeader({
        showCurrentMonth: conversation.showCurrent,
        onShowCurrentMonthChange: conversation.setShowCurrent,
        onRefresh: conversation.refresh,
        loading: conversation.loading,
        showRefreshButton: true,
      })}
      <ToggleButtonGroup
        value={conversationTab}
        exclusive
        size="small"
        onChange={(_event, value) => {
          if (value !== null) setConversationTab(value);
        }}
        sx={{
          p: 0.5,
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          bgcolor: "background.paper",
          "& .MuiToggleButtonGroup-grouped": {
            border: 0,
            borderRadius: "8px !important",
            mx: 0,
          },
          "& .MuiToggleButton-root": {
            flex: "0 0 auto",
            px: { xs: 1.25, sm: 1.75 },
            minHeight: 38,
            textTransform: "none",
            gap: 0.75,
          },
        }}
      >
        <ToggleButton value={0}>
          <Forum fontSize="small" />
          Servicios
        </ToggleButton>
        <ToggleButton value={1}>
          <People fontSize="small" />
          Clientes
        </ToggleButton>
      </ToggleButtonGroup>
      {conversation.loading ? (
        <PageLoadingState label="Cargando conversaciones" compact />
      ) : conversationTab === 0 ? (
        renderConversationsView()
      ) : (
        renderCustomersView()
      )}
    </Stack>
  );

  // Jobs UI is intentionally kept in code but removed from navigation.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderJobsView = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <TextField
            label="Desde"
            type="date"
            value={conversation.dateFrom}
            onChange={(event) => conversation.setDateFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Hasta"
            type="date"
            value={conversation.dateTo}
            onChange={(event) => conversation.setDateTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<Add />}
            variant="contained"
            disabled={conversation.actionLoading}
            onClick={handleOpenJobDialog}
          >
            Crear job
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Jobs
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {sortedJobs.length} resultados
          </Typography>
        </Box>
        <Divider />
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                {renderSortableHeaderCell(
                  "Rango",
                  "dateFrom",
                  jobSort,
                  handleJobSort
                )}
                {renderSortableHeaderCell(
                  "Estado",
                  "status",
                  jobSort,
                  handleJobSort
                )}
                {renderSortableHeaderCell(
                  "Batch",
                  "batchSize",
                  jobSort,
                  handleJobSort,
                  "right"
                )}
                {renderSortableHeaderCell(
                  "Procesados",
                  "processedCount",
                  jobSort,
                  handleJobSort,
                  "right"
                )}
                {renderSortableHeaderCell(
                  "Fallos",
                  "failedCount",
                  jobSort,
                  handleJobSort,
                  "right"
                )}
                {renderSortableHeaderCell(
                  "Creado",
                  "createdAt",
                  jobSort,
                  handleJobSort
                )}
                <TableCell align="right">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleJobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {job.dateFrom} / {job.dateTo}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {job.maxBatches} lote(s) por corrida
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" sx={chipSx} label={job.status} />
                  </TableCell>
                  <TableCell align="right">{job.batchSize}</TableCell>
                  <TableCell align="right">{job.processedCount}</TableCell>
                  <TableCell align="right">{job.failedCount}</TableCell>
                  <TableCell>{formatDate(job.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<PlayArrow />}
                      disabled={
                        conversation.actionLoading ||
                        job.status === "completed" ||
                        job.status === "running"
                      }
                      onClick={() =>
                        conversation.runJob(job.id, job.maxBatches || 1)
                      }
                    >
                      Ejecutar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!visibleJobs.length && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography sx={{ color: "text.secondary", py: 2 }}>
                      No hay jobs creados.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sortedJobs.length}
          page={jobsPagination.page}
          onPageChange={(_event, page) => jobsPagination.setPage(page)}
          rowsPerPage={jobsPagination.rowsPerPage}
          onRowsPerPageChange={(event) =>
            jobsPagination.setRowsPerPage(Number(event.target.value))
          }
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>
    </Box>
  );

  const renderJobDialog = () => {
    if (!jobDraft) return null;

    return (
      <Dialog
        open={jobDialogOpen}
        onClose={() => setJobDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Crear job de analitica</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Desde"
                  type="date"
                  value={jobDraft.dateFrom}
                  onChange={(event) =>
                    updateJobDraft((current) => ({
                      ...current,
                      dateFrom: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hasta"
                  type="date"
                  value={jobDraft.dateTo}
                  onChange={(event) =>
                    updateJobDraft((current) => ({
                      ...current,
                      dateTo: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Batch"
                  type="number"
                  value={jobDraft.batchSize ?? 8}
                  onChange={(event) =>
                    updateJobDraft((current) => ({
                      ...current,
                      batchSize: Number(event.target.value) || 1,
                    }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lotes por corrida"
                  type="number"
                  value={jobDraft.maxBatches ?? 1}
                  onChange={(event) =>
                    updateJobDraft((current) => ({
                      ...current,
                      maxBatches: Number(event.target.value) || 1,
                    }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={jobDraft.runNow === true}
                      onChange={(event) =>
                        updateJobDraft((current) => ({
                          ...current,
                          runNow: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Crear y ejecutar de una vez"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={jobDraft.force === true}
                      onChange={(event) =>
                        updateJobDraft((current) => ({
                          ...current,
                          force: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Reprocesar aunque ya exista análisis"
                />
              </Grid>
            </Grid>

          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setJobDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={jobDraft.runNow ? <PlayArrow /> : <Add />}
            onClick={handleCreateJob}
            disabled={conversation.actionLoading}
          >
            {jobDraft.runNow ? "Crear y ejecutar" : "Crear job"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderCustomerTimelineDialog = () => {
    if (!selectedCustomerPhone) return null;
    const timeline = [...conversation.customerTimeline].sort(
      (left, right) =>
        toTimestamp(left.createdAt ?? left.analyzedAt) -
        toTimestamp(right.createdAt ?? right.analyzedAt)
    );

    return (
      <Dialog
        open={!!selectedCustomerPhone}
        onClose={() => setSelectedCustomerPhone(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Timeline del cliente
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {selectedCustomerPhone}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "background.default" }}>
          {conversation.timelineLoading ? (
            <PageLoadingState label="Cargando línea de tiempo" compact />
          ) : timeline.length ? (
            <Stack spacing={1.5}>
              {timeline.map((service) => (
                <Paper
                  key={service.serviceCode}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 1 }}
                >
                  <Stack spacing={1.25}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {formatDateTime(service.createdAt ?? service.analyzedAt)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {service.serviceCode}
                        </Typography>
                      </Box>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      <Chip
                        size="small"
                        sx={chipSx}
                        color={getOrderTakingStatusColor(service.orderTakingStatus)}
                        label={getOrderTakingStatusLabel(service.orderTakingStatus)}
                      />
                      <Chip
                        size="small"
                        sx={chipSx}
                        color={getSupportTicketsColor(service.supportTicketsCount)}
                        label={getSupportTicketsLabel(service.supportTicketsCount)}
                      />
                      {getVisibleServiceTags(service.serviceTags)
                        .slice(0, 2)
                        .map((tag) => (
                        <Chip
                          key={`${service.serviceCode}-timeline-${tag}`}
                          size="small"
                          sx={chipSx}
                          label={formatServiceTagLabel(tag)}
                        />
                      ))}
                    </Stack>
                  </Stack>

                    <Typography variant="body2">
                      {getServiceSummaryLabel(service)}
                    </Typography>

                    {service.supportDetails?.linkedOrderServiceCode ? (
                      <Alert severity="info">
                        Soporte asociado al pedido{" "}
                        {service.supportDetails.linkedOrderServiceCode}
                      </Alert>
                    ) : null}

                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                      {service.events.slice(0, 8).map((event, index) => (
                        <Chip
                          key={`${service.serviceCode}-${event.type}-${index}`}
                          size="small"
                          sx={chipSx}
                          label={event.label}
                        />
                      ))}
                    </Stack>

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Detalle">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedService(service)}
                          aria-label="Ver detalle"
                          sx={{
                            color: "common.white",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Visibility fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No hay servicios analizados para este cliente.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSelectedCustomerPhone(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderServiceDetailDialog = () => {
    if (!selectedService) return null;
  const service = selectedService;
  const supportTicketCode = service.supportDetails.ticketCode;
  const supportTicketReason = getSupportCategoryLabel(
    service.supportDetails.category,
  );
  const frictionLabel =
    service.frictionType === "none"
      ? "Sin fricción"
      : FRICTION_LABELS[service.frictionType];
  const frictionDetail =
    service.frictionType === "none"
      ? frictionLabel
      : service.frictionReason || frictionLabel;
  const deterministicFrictionLabel =
    service.deterministicFrictionType &&
    service.deterministicFrictionType !== service.frictionType
      ? FRICTION_LABELS[service.deterministicFrictionType]
      : "";
  const frictionSignals = service.frictionSignals ?? [];
  const frictionSub = [
    frictionDetail,
    deterministicFrictionLabel && deterministicFrictionLabel !== frictionLabel
      ? `Regla base: ${deterministicFrictionLabel}`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const relatedServicesCount = relatedServices.length;

    return (
      <Dialog
        open={!!selectedService}
        onClose={() => setSelectedService(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack spacing={1}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {service.phoneNumber}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {service.serviceCode}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  size="small"
                  sx={chipSx}
                  color={getOrderTakingStatusColor(service.orderTakingStatus)}
                  label={getOrderTakingStatusLabel(service.orderTakingStatus)}
                />
                <Chip
                  size="small"
                  sx={chipSx}
                  color={getSupportTicketsColor(service.supportTicketsCount)}
                  label={getSupportTicketsLabel(service.supportTicketsCount)}
                />
                {getVisibleServiceTags(service.serviceTags)
                  .slice(0, 2)
                  .map((tag) => (
                  <Chip
                    key={`${service.serviceCode}-header-${tag}`}
                    size="small"
                    sx={chipSx}
                    label={formatServiceTagLabel(tag)}
                  />
                ))}
              </Stack>
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {getServiceSummaryLabel(service)}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "background.default" }}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} lg={7}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Resumen rápido
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{
                      overflowX: "auto",
                      pb: 0.5,
                      pr: 0.5,
                      alignItems: "stretch",
                    }}
                  >
                    <InsightCard
                      label="Resumen del servicio"
                      value={service.summary || getServiceSummaryLabel(service) || "-"}
                      minWidth={320}
                    />
                    <InsightCard
                      label="Toma pedidos"
                      value={
                        <Chip
                          size="small"
                          sx={chipSx}
                          color={getOrderTakingStatusColor(service.orderTakingStatus)}
                          label={getOrderTakingStatusLabel(service.orderTakingStatus)}
                        />
                      }
                      sub={
                        service.orderTakingStatus === "sent"
                          ? "El pedido quedó enviado a preparación."
                          : service.orderTakingStatus === "error"
                            ? "Hubo intentó de pedido, pero no quedó enviado a preparación."
                            : "No hubo intentó de pedido."
                      }
                      minWidth={240}
                    />
                    <InsightCard
                      label="Hubo fricción"
                      value={
                        <Chip
                          size="small"
                          sx={chipSx}
                          color={service.frictionType === "none" ? "default" : "warning"}
                          label={yesNoLabel(service.frictionType !== "none")}
                        />
                      }
                      sub={frictionSub}
                      minWidth={220}
                    />
                    <InsightCard
                      label="Señales de fricción"
                      value={
                        frictionSignals.length ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {frictionSignals.slice(0, 4).map((signal) => (
                              <Chip
                                key={`${service.serviceCode}-friction-${signal}`}
                                size="small"
                                sx={chipSx}
                                label={formatFrictionSignalLabel(signal)}
                              />
                            ))}
                            {frictionSignals.length > 4 ? (
                              <Chip size="small" sx={chipSx} label={`+${frictionSignals.length - 4}`} />
                            ) : null}
                          </Stack>
                        ) : (
                          <Chip
                            size="small"
                            sx={chipSx}
                            color="default"
                            label="Sin señales"
                          />
                        )
                      }
                      sub={
                        frictionSignals.length
                          ? "Señales semánticas para depurar prompts y herramientas."
                          : "No se detectaron señales adicionales."
                      }
                      minWidth={300}
                    />
                    <InsightCard
                      label="Tickets soporte"
                      value={
                        <Stack spacing={0.75}>
                          <Chip
                            size="small"
                            sx={chipSx}
                            color={getSupportTicketsColor(service.supportTicketsCount)}
                            label={getSupportTicketsLabel(service.supportTicketsCount)}
                          />
                          {supportTicketCode ? (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {supportTicketCode}
                            </Typography>
                          ) : null}
                        </Stack>
                      }
                      sub={
                        service.supportTicketsCount > 0 ? supportTicketReason : undefined
                      }
                      minWidth={220}
                    />
                    <InsightCard
                      label="Tags"
                      value={
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {getVisibleServiceTags(service.serviceTags)
                            .slice(0, 4)
                            .map((tag) => (
                            <Chip
                              key={`${service.serviceCode}-detail-${tag}`}
                              size="small"
                              sx={chipSx}
                              label={formatServiceTagLabel(tag)}
                            />
                          ))}
                          {getVisibleServiceTags(service.serviceTags).length > 4 ? (
                            <Chip
                              size="small"
                              sx={chipSx}
                              label={`+${
                                getVisibleServiceTags(service.serviceTags).length - 4
                              }`}
                            />
                          ) : null}
                        </Stack>
                      }
                      sub={`${getVisibleServiceTags(service.serviceTags).length} etiquetas`}
                      minWidth={340}
                    />
                    <InsightCard
                      label="Siguiente acción"
                      value={service.nextBestAction || "-"}
                      minWidth={280}
                    />
                    <InsightCard
                      label="Cierre"
                      value={service.closureReason || "-"}
                      minWidth={280}
                    />
                    <InsightCard
                      label="Motivo no resuelto"
                      value={service.unresolvedReason || "-"}
                      minWidth={280}
                    />
                  </Stack>
                </Box>

                <DetailSection title="Secuencia del cliente">
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {relatedServicesCount > 1
                      ? `${relatedServicesCount} servicios en esta secuencia cronológica.`
                      : "Solo hay un servicio en este rango para este cliente."}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1.25}
                    sx={{
                      overflowX: "auto",
                      pb: 0.5,
                      pr: 0.5,
                      alignItems: "stretch",
                    }}
                  >
                    {relatedServices.map((relatedService) => {
                      const selected =
                        relatedService.serviceCode === service.serviceCode;
                      return (
                        <InsightCard
                          key={relatedService.serviceCode}
                          label={formatDateTime(
                            relatedService.createdAt ?? relatedService.analyzedAt
                          )}
                          value={
                            <Stack spacing={0.75}>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                <Chip
                                  size="small"
                                  sx={chipSx}
                                  color={getOrderTakingStatusColor(relatedService.orderTakingStatus)}
                                  label={getOrderTakingStatusLabel(
                                    relatedService.orderTakingStatus
                                  )}
                                />
                                <Chip
                                  size="small"
                                  sx={chipSx}
                                  color={getSupportTicketsColor(
                                    relatedService.supportTicketsCount
                                  )}
                                  label={getSupportTicketsLabel(
                                    relatedService.supportTicketsCount
                                  )}
                                />
                                {getVisibleServiceTags(relatedService.serviceTags)
                                  .slice(0, 2)
                                  .map((tag) => (
                                  <Chip
                                    key={`${relatedService.serviceCode}-sequence-${tag}`}
                                    size="small"
                                    sx={chipSx}
                                    label={formatServiceTagLabel(tag)}
                                  />
                                ))}
                              </Stack>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {relatedService.serviceCode}
                              </Typography>
                            </Stack>
                          }
                          sub={getServiceSummaryLabel(relatedService)}
                          minWidth={250}
                          selected={selected}
                          onClick={() => setSelectedService(relatedService)}
                        />
                      );
                    })}
                  </Stack>
                </DetailSection>

                <DetailSection title="Eventos semánticos">
                  {service.events.length ? (
                    <Stack spacing={1}>
                      {service.events.map((event, index) => (
                        <Box key={`${event.type}-${index}`}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" sx={chipSx} label={event.type} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {event.label}
                            </Typography>
                          </Stack>
                          {event.messageIndexStart != null ? (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Mensaje {event.messageIndexStart}
                              {event.messageIndexEnd != null
                                ? ` - ${event.messageIndexEnd}`
                                : ""}
                            </Typography>
                          ) : null}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Sin eventos semánticos.
                    </Typography>
                  )}
                </DetailSection>

                <DetailSection title="Evidencias">
                  {service.evidenceRefs.length ? (
                    service.evidenceRefs.map((ref) => (
                      <Box key={`${ref.messageIndexStart}-${ref.reason}`}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Mensajes {ref.messageIndexStart}
                          {ref.messageIndexEnd != null
                            ? ` - ${ref.messageIndexEnd}`
                            : ""}
                        </Typography>
                        <Typography variant="body2">{ref.reason}</Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Sin evidencias resumidas.
                    </Typography>
                  )}
                </DetailSection>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={5}>
              <DetailSection title="Conversacion embebida">
                <Stack spacing={1.25}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {selectedServiceMessages.messages.length} mensajes |{" "}
                    {selectedService.serviceCode}
                  </Typography>
                  {soulLoading || selectedServiceMessages.loading ? (
                    <PageLoadingState label="Cargando mensajes" compact />
                  ) : selectedServiceMessages.error ? (
                    <Alert severity="error">{selectedServiceMessages.error}</Alert>
                  ) : selectedServiceMessages.messages.length ? (
                    <Box
                      sx={{
                        maxHeight: { xs: 460, lg: 720 },
                        overflowY: "auto",
                        pr: 1,
                      }}
                    >
                      <Stack spacing={1.25}>
                        {selectedServiceMessages.messages.map((message, index) => {
                          const isAssistantSide =
                            message.role === TypeRoleEnum.SYSTEM ||
                            message.role === TypeRoleEnum.ASSISTANT ||
                            message.role === TypeRoleEnum.TOOL;
                          const isToolMessage =
                            message.messageType === MessageTypeEnum.TOOL;
                          const roleLabel =
                            message.role === TypeRoleEnum.USER
                              ? "Cliente"
                              : message.role === TypeRoleEnum.ASSISTANT
                                ? "IA"
                                : message.role === TypeRoleEnum.SYSTEM
                                  ? "Sistema"
                                  : "Herramienta";

                          return (
                            <Box
                              key={`${selectedService.serviceCode}-${index}`}
                              display="flex"
                              flexDirection="column"
                              alignItems={isAssistantSide ? "flex-end" : "flex-start"}
                            >
                              {isToolMessage ? (
                                <Box
                                  sx={{
                                    width: "100%",
                                    maxWidth: { xs: "100%", sm: 420 },
                                  }}
                                >
                                  <MessageItem
                                    message={message}
                                    toolDisplayMode="compact"
                                  />
                                </Box>
                              ) : (
                                <Paper
                                  elevation={2}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: "18px",
                                    borderBottomRightRadius: isAssistantSide
                                      ? "4px"
                                      : "18px",
                                    borderBottomLeftRadius: isAssistantSide
                                      ? "18px"
                                      : "4px",
                                    bgcolor: isAssistantSide
                                      ? "grey.200"
                                      : "primary.main",
                                    color: isAssistantSide ? "black" : "white",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                    maxWidth: { xs: "100%", sm: "78%" },
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: isAssistantSide
                                      ? "flex-end"
                                      : "flex-start",
                                  }}
                                >
                                  <MessageItem message={message} />
                                </Paper>
                              )}
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                                mt={0.5}
                                flexWrap="wrap"
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "12px",
                                  }}
                                >
                                  {formatDateTime(message.createdAt)}
                                </Typography>
                                <Chip size="small" sx={chipSx} label={roleLabel} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No se encontraron mensajes embebidos para este servicio.
                    </Typography>
                  )}
                </Stack>
              </DetailSection>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSelectedService(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderActiveSection = () => {
    if (section === "summary") {
      return renderSummaryView();
    }
    return renderConversationSection();
  };

  return (
    <Box
      sx={{
        ...dashboardPageSx,
        minHeight: { xs: "calc(100dvh - 56px)", md: "calc(100dvh - 64px)" },
        ...dashboardPagePaddingSx,
        px: { xs: 2, sm: 2 },
      }}
    >
      <Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
        <ToggleButtonGroup
          value={section}
          exclusive
          size="small"
          onChange={(_event, value) => {
            if (!value) return;
            navigate(
              value === "summary" ? "/dashboard" : "/dashboard/conversations",
            );
          }}
          sx={{
          p: 0.5,
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            bgcolor: "background.paper",
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              borderRadius: "8px !important",
              mx: 0,
            },
            "& .MuiToggleButton-root": {
              flex: "0 0 auto",
              px: { xs: 1.25, sm: 1.75 },
              minHeight: 38,
              textTransform: "none",
              gap: 0.75,
            },
          }}
        >
          <ToggleButton value="summary">
            <AutoGraph fontSize="small" />
            Resumen
          </ToggleButton>
          <ToggleButton value="conversations">
            <Psychology fontSize="small" />
            Insights de Pedidos
          </ToggleButton>
        </ToggleButtonGroup>

        {section === "conversations" && conversation.error ? (
          <Alert severity="error">{conversation.error}</Alert>
        ) : null}
        <Paper variant="outlined" sx={analyticsContentPanelSx}>
          {renderActiveSection()}
        </Paper>
      </Stack>

      {renderJobDialog()}
      {renderCustomerTimelineDialog()}
      {renderServiceDetailDialog()}
      <FeedbackSnackbar
        open={copyFeedback.open}
        message={copyFeedback.message}
        severity={copyFeedback.severity}
        onClose={() =>
          setCopyFeedback((current) => ({
            ...current,
            open: false,
          }))
        }
      />
    </Box>
  );
};

export default AnalyticsPage;
